import {
    Box,
    ChakraProps,
    Select,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Text,
    VStack
} from "@chakra-ui/react";
import ru from "date-fns/locale/ru";
import enUS from "date-fns/locale/en-US";
import {ErrorMessage, useField, useFormikContext} from "formik";
import React, {FC, useState} from "react";
import ReactDatePicker, {ReactDatePickerProps, registerLocale} from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import "./date-picker.css";

/**
 * Компонент для выбора и устоановки даты и времени
 * @param param0
 * @returns
 */
export const DatePickerStyled: FC<Omit<ReactDatePickerProps, "onChange">
    & {
    specialLabel?: string,
    customStyle?: ChakraProps,
    isDisabled?: boolean,
    optionalAction?: Function,
    setDisableClose?: (value: boolean) => void
}> = (props) => {
    registerLocale("ru", ru);
    const {setFieldValue} = useFormikContext();
    const [field] = useField(props as unknown as any);

    const DatePickerStyledConfig: ChakraProps = {}

    return (
        <Box className="light-theme" {...DatePickerStyledConfig}>
            <Text fontSize="14px" color="gray.400" mb="2px">{props.title}</Text>
            <ReactDatePicker
                selected={(field.value && new Date(field.value)) || null}
                onChange={val => {
                    if (val === null && field.name === "start_date") {
                        return;
                    }
                    if (val === null) {
                        setFieldValue(field.name, val)
                        return;
                    }
                    //@ts-ignore
                    let newVal = new Date(val)
                    newVal.setHours(0, 0, 0, 0);
                    setFieldValue(field.name, newVal)
                    if (props?.optionalAction) {
                        props?.optionalAction()
                    }
                    props.setDisableClose?.(false);
                }}

                disabled={props.isDisabled}
                locale={ru}
                dateFormat={"dd.MM.yyyy"}
                minDate={props?.minDate}
                showYearPicker={false}
                showYearDropdown={false}
                portalId="root-portal"
                isClearable={props?.isClearable}
                onCalendarOpen={() => props.setDisableClose?.(true)} // Отключаем закрытие Popover
                onCalendarClose={() => props.setDisableClose?.(false)} // Включаем обратно
                // popperProps={{ modifiers: [{ name: "preventOverflow", options: { boundary: "viewport" } }] }}
            />
            <Box className="specialError">
                <ErrorMessage name={field.name}/>
            </Box>
        </Box>
    )
}

export const DatePickerSlider = ({ onChange }: { onChange: (date: string) => void }) => {
    const [year, setYear] = useState(new Date().getFullYear());
    const days = Array.from({ length: 31 }, (_, i) => i + 1);
    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    return (
        <VStack>
            <Select onChange={(e) => onChange(`${e.target.value}.${months[0]}.${year}`)}>
                {days.map((day) => (
                    <option key={day} value={day}>{day}</option>
                ))}
            </Select>
            <Select onChange={(e) => onChange(`${days[0]}.${e.target.value}.${year}`)}>
                {months.map((month, index) => (
                    <option key={month} value={index + 1}>{month}</option>
                ))}
            </Select>
            <Slider
                defaultValue={year}
                min={1900}
                max={new Date().getFullYear()}
                step={1}
                onChange={(val) => {
                    setYear(val);
                    onChange(`${days[0]}.${months[0]}.${val}`);
                }}
            >
                <SliderTrack>
                    <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
            </Slider>
        </VStack>
    );
};

/**
 * Сброс глобальных правил из `date-picker.css` (рамка/40px у `.react-datepicker__input-container`),
 * чтобы не было «второго» поля внутри обёртки Chakra.
 */
const datePickerUpdateStyledShellSx = {
    position: "relative",
    /** иначе обрезается выпадающий календарь (Collapse / Grid) */
    overflow: "visible",
    "& .react-datepicker-wrapper": {
        width: "100%",
        display: "block",
    },
    "& .react-datepicker__input-container": {
        border: "none !important",
        padding: "0 !important",
        margin: 0,
        height: "auto",
        minHeight: 0,
        minWidth: "0 !important",
        width: "100%",
        backgroundColor: "transparent !important",
        boxShadow: "none !important",
        borderRadius: 0,
    },
    "& .react-datepicker__input-container:hover": {
        borderColor: "transparent",
    },
    "& .react-datepicker__input-container:focus-within": {
        zIndex: 1,
        boxShadow: "none !important",
        borderColor: "transparent !important",
    },
    "& .react-datepicker__input-container > input": {
        boxSizing: "border-box",
        width: "100%",
        height: "auto",
        minHeight: "24px",
        backgroundColor: "transparent !important",
    },
    "& .react-datepicker__input-container:has(.react-datepicker__close-icon) > input": {
        paddingRight: "32px",
    },
    /**
     * Библиотека задаёт свой `top` у крестика — без !important вертикальный центр «плывёт».
     * Ближе к правому краю поля, чем дефолт react-datepicker.
     */
    "& .react-datepicker__close-icon": {
        backgroundColor: "transparent !important",
        top: "46% !important",
        bottom: "auto !important",
        left: "auto !important",
        right: "-1px !important",
        transform: "translateY(-50%) !important",
        margin: "0 !important",
        padding: "0 !important",
        display: "flex !important",
        alignItems: "center",
        justifyContent: "center",
    },
    "& .react-datepicker__close-icon::after": {
        backgroundColor: "transparent !important",
        fontSize: "1.6rem",
        width: "22px",
        height: "22px",
        lineHeight: "22px",
        margin: "0 auto",
    },
};

/**
 * Компонент для выбора и установки даты и времени
 * @returns
 * @param props
 */
export const DatePickerUpdateStyled: FC<Omit<ReactDatePickerProps, "onChange"> & {
    specialLabel?: string;
    customStyle?: ChakraProps;
    /** Стили внешней обёртки (как `FilterDefaultStyleConfig` в фильтрах) */
    wrapperProps?: ChakraProps;
    isDisabled?: boolean;
    optionalAction?: Function;
    title?: string;
    placeholderText?: string;
    onChange: (value: Date | null) => void;
    error?: string;
}> = (props) => {
    registerLocale("ru", ru);

    const {
        onChange,
        customStyle,
        wrapperProps,
        isDisabled,
        placeholderText,
        minDate,
        maxDate,
        isClearable,
        selected: selectedProp,
        dateFormat,
        locale,
        specialLabel: _specialLabel,
        optionalAction: _optionalAction,
        title: _title,
        error: _error,
        popperProps: userPopperProps,
        ...restPicker
    } = props;

    const [uncontrolled, setUncontrolled] = useState<Date | null>(null);
    const isControlled = selectedProp !== undefined;
    const selected = isControlled ? selectedProp : uncontrolled;

    const handleDateChange = (value: Date | null) => {
        if (!isControlled) {
            setUncontrolled(value);
        }
        onChange(value);
    };

    const DatePickerStyledConfig: ChakraProps = {
        maxW: "200px",
        ...customStyle,
        ...wrapperProps,
    };

    const {sx: wrapperSx, ...boxRest} = DatePickerStyledConfig as ChakraProps & {sx?: ChakraProps["sx"]};

    return (
        <Box
            className="light-theme"
            {...boxRest}
            sx={{
                ...datePickerUpdateStyledShellSx,
                ...(typeof wrapperSx === "object" && wrapperSx !== null && !Array.isArray(wrapperSx)
                    ? wrapperSx
                    : {}),
            }}
        >
            <ReactDatePicker
                {...restPicker}
                selected={selected}
                onChange={handleDateChange}
                disabled={isDisabled}
                placeholderText={placeholderText}
                locale={locale ?? enUS}
                dateFormat={dateFormat ?? "MM-dd-yyyy"}
                minDate={minDate}
                maxDate={maxDate}
                showYearPicker={false}
                showYearDropdown={false}
                isClearable={isClearable}
                popperProps={{
                    strategy: "fixed",
                    ...userPopperProps,
                }}
            />
        </Box>
    );
};

export const DatePickerRangeStyled: FC<Omit<ReactDatePickerProps, "onChange"> & {
    specialLabel?: string;
    customStyle?: ChakraProps;
    isDisabled?: boolean;
    optionalAction?: Function;
    title?: string;
    onChange: (value: Date | null) => void;
    error?: string;
}> = (props) => {
    registerLocale("ru", ru);

    const DatePickerStyledConfig: ChakraProps = {
        maxW: "400px",
    };
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    const handleDateChange = (value: Date | null) => {
        setSelectedDate(value); // Обновляем локальное состояние
        props.onChange(value); // Вызываем переданную функцию onChange
    };
    return (
        <Box  {...DatePickerStyledConfig} className="light-theme">
            <ReactDatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                disabled={props.isDisabled}
                placeholderText={'From date'}
                locale={enUS}
                dateFormat={"dd.MM.yyyy"}
                minDate={props.minDate}
                showYearPicker={false}
                showYearDropdown={false}
                isClearable={props.isClearable}
            />
            <ReactDatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                disabled={props.isDisabled}
                placeholderText={'End date'}
                locale={enUS}
                dateFormat={"dd.MM.yyyy"}
                minDate={props.minDate}
                showYearPicker={false}
                showYearDropdown={false}
                isClearable={props.isClearable}
            />
        </Box>
    );
};

export const TimePickerStyled: FC<Omit<ReactDatePickerProps, "onChange">
    & { specialLabel?: string, customStyle?: ChakraProps, isDisabled?: boolean }> = (props) => {
    const {setFieldValue} = useFormikContext();
    const [field] = useField(props as unknown as any);
    const DatePickerStyledConfig: ChakraProps = {
        maxW: "125px",
        minW: "110px",
        mt: props.specialLabel ? "8px" : "5px",
        minH: "45px",
        pr: "8px",
        pl: "8px",
        ...props.customStyle
    }


    return (
        <Box {...DatePickerStyledConfig} className="light-theme" display="flex" flexDir="column"
             justifyContent="center">
            {props.specialLabel && <Text fontSize="14px" color="gray.400" mb="2px">{props.specialLabel}</Text>}
            <ReactDatePicker
                selected={(field.value && new Date(field.value)) || null}
                onChange={val => {
                    setFieldValue(field.name, val)
                }}
                disabled={props.isDisabled}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={60}
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                wrapperClassName="time-wrapper"
                timeCaption="Время"
                className="time"
                name={props.name}
            />
            <Box className="specialError">
                <ErrorMessage name={field.name}/>
            </Box>
        </Box>
    )
}

